import { Button, Stack, styled, Typography } from '@mui/material';
import { CheckCircle, Description as DescriptionIcon } from '@mui/icons-material';
import React from 'react';
import { ChatRequest } from './Interface';
import { sanitizeNote } from 'op-pages/RO/Notes/sanitizeNote';
import { getDocumentDisplayName, getDocumentSummaryMessage, isDocumentType } from './utils/documentTypes';

interface ProstateMRIReferralEditChatMessageProps {
  message: ChatRequest;
  onEditClick: () => void;
}

interface DocumentEditChatMessageProps {
  message: ChatRequest;
  documentType: string;
  onEditClick: () => void;
}

const ChatContentHTML = styled('div')<{ $format: boolean }>`
  line-height: 24px;
  font-size: 16px;
  white-space: ${({ $format }) => ($format ? 'normal' : 'pre-wrap')};
  color: ${(props) => props.theme.palette.text.primary};
  padding-left: 0px;
  padding-top: 16px;
  word-break: break-word;

  font,
  ul {
    margin-left: 32px !important;
  }

  ol {
    margin-left: 8px;
  }

  li {
    margin-bottom: 8px;
    list-style-type: disc;
  }

  p {
    margin-bottom: 0px;
    padding-top: 8px;
  }

  b {
    color: ${(props) => props.theme.palette.text.primary};
    margin: 0px !important;
  }

  div {
    margin-left: 0px;
  }
`;

export const ProstateMRIReferralEditChatMessage = ({
  message,
  onEditClick,
}: ProstateMRIReferralEditChatMessageProps): React.ReactNode => {
  return (
    <Stack direction="column" alignItems="flex-start" gap={1}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<CheckCircle />}
        sx={{ width: 'fit-content', borderRadius: '4px', color: 'primary.main' }}
        onClick={onEditClick}>
        <Typography>DRAFTED IMAGING REQUEST</Typography>
      </Button>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
        {message.response}
      </Typography>
    </Stack>
  );
};

/**
 * Generic document edit chat message component
 * Uses the document type configuration to display the appropriate pill and summary
 * For edits (isEdit=true), shows only the LLM's actual response text (no pill)
 * For generation (isEdit=false/undefined), shows the pill and summary message
 */
export const DocumentEditChatMessage = ({
  message,
  documentType,
  onEditClick,
}: DocumentEditChatMessageProps): React.ReactNode => {
  const displayName = getDocumentDisplayName(documentType);
  const summaryMessage = getDocumentSummaryMessage(documentType);

  // If this is an edit, show only the LLM's actual response text (no pill, no link styling, no extra spacing)
  if (message.isEdit) {
    // Render plain text without extra spacing - don't use ChatContentHTML which adds padding
    return (
      <Typography
        variant="body1"
        sx={{
          whiteSpace: 'pre-wrap',
          paddingTop: 0,
          marginTop: 0,
          lineHeight: '24px',
          fontSize: '16px',
          wordBreak: 'break-word',
        }}>
        {message.response}
      </Typography>
    );
  }

  // For generation, show the pill and summary message
  return (
    <Stack direction="column" alignItems="flex-start" gap={1}>
      <Button
        variant="outlined"
        size="small"
        color="primary"
        startIcon={<DescriptionIcon />}
        sx={{
          width: 'fit-content',
          borderRadius: '4px',
          color: 'primary.main',
          textTransform: 'none',
        }}
        onClick={onEditClick}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
          {`Draft: ${displayName}`}
        </Typography>
      </Button>
      <ChatContentHTML $format={true} dangerouslySetInnerHTML={sanitizeNote(summaryMessage, true)} />
    </Stack>
  );
};

// Legacy export for backwards compatibility
export const InitialConsultLetterEditChatMessage = ({
  message,
  onEditClick,
}: DocumentEditChatMessageProps): React.ReactNode => {
  return <DocumentEditChatMessage message={message} documentType="hpi_document" onEditClick={onEditClick} />;
};

/**
 * Helper to check if a message type should use the document editor
 */
export { isDocumentType };
